import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Play, Sparkles, Clock, BookOpen, X, ArrowRight, Video as VideoIcon, Lock } from 'lucide-react';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useAuth } from '@/context/AuthContext';

/**
 * /learn — Vendor Academy.
 *
 * UI shell only. Each lesson has a `video_url`; when null we render a
 * "Coming soon" tile with a soft animated placeholder. Once the videos are
 * generated (Sora 2 → MP4) we just paste the URLs into the LESSONS array
 * below and the player picks them up automatically.
 */

const TOPICS = [
  { id: 'all',        label: 'All lessons',        accent: 'from-fuchsia-400 to-cyan-300' },
  { id: 'start',      label: 'Getting Started',    accent: 'from-emerald-400 to-cyan-300' },
  { id: 'ideas',      label: 'What to Sell',       accent: 'from-amber-300 to-orange-400' },
  { id: 'listing',    label: 'Listing Quality',    accent: 'from-fuchsia-400 to-pink-400' },
  { id: 'pricing',    label: 'Pricing & Payouts',  accent: 'from-violet-400 to-blue-400' },
  { id: 'growth',     label: 'Promotion & Growth', accent: 'from-cyan-300 to-fuchsia-400' },
];

const LESSONS = [
  {
    id: 'first-listing',
    topic: 'start',
    title: 'Your First Listing in 60 Seconds',
    summary: 'From "Become a Vendor" click to a live service in under a minute. Watch a vendor go from blank profile to bookable listing.',
    duration: '0:60',
    chips: ['Onboarding', 'Beginner'],
    thumb: { from: '#10B981', to: '#06B6D4' },
    video_url: null,
    poster_url: null,
    cta: { label: 'Start your profile', to: '/onboarding/vendor' },
  },
  {
    id: 'what-to-sell',
    topic: 'ideas',
    title: 'What Can You Actually Sell?',
    summary: '10 service ideas in 90 seconds — regalia, photography, voiceovers, beat-making, tax prep, fry bread catering, and more.',
    duration: '1:30',
    chips: ['Inspiration', 'Idea Spark'],
    thumb: { from: '#F59E0B', to: '#EF4444' },
    video_url: null,
    poster_url: null,
    cta: { label: 'Browse the marketplace', to: '/browse' },
  },
  {
    id: 'anatomy',
    topic: 'listing',
    title: 'The Anatomy of a Booked Listing',
    summary: 'Side-by-side: what photos, descriptions, intro videos, and pricing turn a "weak" listing into a booking magnet.',
    duration: '1:15',
    chips: ['Conversion', 'Polish'],
    thumb: { from: '#A855F7', to: '#EC4899' },
    video_url: null,
    poster_url: null,
    cta: { label: 'Edit your profile', to: '/dashboard/vendor/profile' },
  },
  {
    id: 'pricing',
    topic: 'pricing',
    title: 'Pricing That Books — Not Bargains',
    summary: 'How to price by hour, package, or value. Tier strategy that nets bigger jobs without scaring off first-time buyers.',
    duration: '1:00',
    chips: ['Pricing', 'Strategy'],
    thumb: { from: '#8B5CF6', to: '#3B82F6' },
    video_url: null,
    poster_url: null,
    cta: { label: 'Add a service', to: '/dashboard/vendor/services' },
  },
  {
    id: 'payouts',
    topic: 'pricing',
    title: 'Profile to Payout: How Money Flows',
    summary: 'The path from booking confirmed → work delivered → Stripe payout in your bank. Zero buyer fees, full transparency.',
    duration: '1:20',
    chips: ['Stripe', 'Trust'],
    thumb: { from: '#22D3EE', to: '#3B82F6' },
    video_url: null,
    poster_url: null,
    cta: { label: 'Connect Stripe', to: '/dashboard/vendor/profile' },
  },
  {
    id: 'first-100',
    topic: 'growth',
    title: 'Your First 100 Followers',
    summary: 'Stories, posts, reposts, and the Feed. How to use the Social Layer to drive your first wave of profile visits.',
    duration: '1:10',
    chips: ['Social', 'Growth'],
    thumb: { from: '#EC4899' , to: '#8B5CF6' },
    video_url: null,
    poster_url: null,
    cta: { label: 'Open The Feed', to: '/feed' },
  },
];

function VideoPlayerModal({ lesson, onClose }) {
  if (!lesson) return null;
  const isReady = !!lesson.video_url;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
      onClick={onClose}
      data-testid="learn-video-modal"
    >
      <div
        className="relative w-full max-w-4xl rounded-3xl overflow-hidden bg-[#0B0D2B] border border-white/10 shadow-[0_0_80px_rgba(168,85,247,0.25)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/50 hover:bg-black/80 text-white"
          data-testid="learn-video-close"
        >
          <X size={18} />
        </button>

        {isReady ? (
          <video
            src={lesson.video_url}
            poster={lesson.poster_url || undefined}
            controls
            autoPlay
            playsInline
            className="w-full aspect-video bg-black"
            data-testid={`learn-video-player-${lesson.id}`}
          />
        ) : (
          <div
            className="w-full aspect-video flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${lesson.thumb.from}, ${lesson.thumb.to})` }}
            data-testid={`learn-video-placeholder-${lesson.id}`}
          >
            <div className="text-center px-8">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center border border-white/30">
                <Lock size={28} className="text-white" />
              </div>
              <h3 className="text-white font-syne text-2xl font-bold">Coming Soon</h3>
              <p className="text-white/85 text-sm mt-2 max-w-md mx-auto">
                We're rendering this episode in our brand-matched animated style. It'll appear here automatically — no refresh needed.
              </p>
            </div>
          </div>
        )}

        <div className="p-6 border-t border-white/10">
          <h3 className="font-syne text-2xl font-bold text-white">{lesson.title}</h3>
          <p className="text-zinc-400 text-sm mt-2">{lesson.summary}</p>
          {lesson.cta && (
            <Link
              to={lesson.cta.to}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white text-sm font-bold hover:shadow-[0_0_28px_rgba(168,85,247,0.5)] transition-all"
              data-testid={`learn-video-cta-${lesson.id}`}
            >
              {lesson.cta.label} <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function LessonCard({ lesson, onPlay }) {
  const isReady = !!lesson.video_url;
  return (
    <button
      type="button"
      onClick={() => onPlay(lesson)}
      className="group relative text-left rounded-2xl overflow-hidden bg-[#13153A]/60 border border-white/10 hover:border-fuchsia-400/40 hover:-translate-y-0.5 transition-all duration-300"
      data-testid={`learn-card-${lesson.id}`}
    >
      {/* Thumb */}
      <div
        className="relative aspect-video w-full overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${lesson.thumb.from}, ${lesson.thumb.to})` }}
      >
        {/* Animated grain / soft orbs */}
        <div className="absolute inset-0 opacity-40 mix-blend-screen pointer-events-none">
          <div className="absolute -top-12 -left-8 w-48 h-48 rounded-full bg-white/30 blur-3xl animate-pulse" />
          <div className="absolute -bottom-8 -right-10 w-56 h-56 rounded-full bg-black/30 blur-3xl" />
        </div>

        {/* Play badge */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-white/15 backdrop-blur-md border border-white/40 flex items-center justify-center group-hover:scale-110 transition-transform shadow-[0_8px_32px_rgba(0,0,0,0.35)]">
            {isReady ? (
              <Play size={26} className="text-white fill-white ml-1" />
            ) : (
              <Lock size={22} className="text-white" />
            )}
          </div>
        </div>

        {/* Duration pill */}
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/55 backdrop-blur text-white text-[10px] font-bold">
          <Clock size={10} /> {lesson.duration}
        </span>

        {/* Coming Soon ribbon */}
        {!isReady && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/55 text-amber-200 text-[10px] font-bold border border-amber-300/40">
            COMING SOON
          </span>
        )}
      </div>

      {/* Body */}
      <div className="p-4">
        <h3 className="font-syne text-base font-bold text-white leading-tight group-hover:text-fuchsia-200 transition-colors">
          {lesson.title}
        </h3>
        <p className="text-xs text-zinc-400 mt-2 line-clamp-2">{lesson.summary}</p>
        <div className="mt-3 flex items-center gap-1.5 flex-wrap">
          {lesson.chips.map((c) => (
            <span key={c} className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[10px] text-zinc-300 font-semibold">
              {c}
            </span>
          ))}
        </div>
      </div>
    </button>
  );
}

export default function LearnPage() {
  const { user } = useAuth();
  const [topic, setTopic] = useState('all');
  const [active, setActive] = useState(null);

  const filtered = useMemo(
    () => (topic === 'all' ? LESSONS : LESSONS.filter((l) => l.topic === topic)),
    [topic],
  );

  const readyCount = LESSONS.filter((l) => l.video_url).length;

  return (
    <div className="min-h-screen bg-[#0B0D2B] text-white">
      <Navbar />

      <main className="max-w-6xl mx-auto px-4 pt-24 pb-16" data-testid="learn-page">
        {/* Hero */}
        <section className="relative mb-10 sm:mb-14 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#1A1A3E]/90 to-[#0B0D2B] p-8 sm:p-12">
          {/* Decorative orbs */}
          <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-fuchsia-500/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-cyan-400/15 blur-3xl pointer-events-none" />

          <div className="relative">
            <p className="inline-flex items-center gap-2 text-xs text-fuchsia-300 font-bold tracking-widest uppercase mb-3">
              <BookOpen size={14} /> Vendor Academy
            </p>
            <h1 className="font-syne font-bold leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
              Sell smarter.
              <br />
              <span className="bg-gradient-to-r from-fuchsia-400 via-pink-400 to-cyan-300 bg-clip-text text-transparent">
                In bite-sized lessons.
              </span>
            </h1>
            <p className="text-zinc-300 text-base mt-5 max-w-2xl">
              Short, animated walkthroughs covering everything Indigenous vendors actually need —
              from your first listing to your hundredth booking. Made for the EntrepreNatives
              community, in our voice.
            </p>

            <div className="mt-6 flex items-center gap-3 flex-wrap">
              {user ? (
                <Link
                  to="/dashboard/vendor/profile"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white text-sm font-bold hover:shadow-[0_0_28px_rgba(168,85,247,0.55)] transition-all"
                  data-testid="learn-hero-cta-primary"
                >
                  Open my dashboard <ArrowRight size={14} />
                </Link>
              ) : (
                <Link
                  to="/onboarding/vendor"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white text-sm font-bold hover:shadow-[0_0_28px_rgba(168,85,247,0.55)] transition-all"
                  data-testid="learn-hero-cta-primary"
                >
                  Become a Vendor <ArrowRight size={14} />
                </Link>
              )}
              <span className="inline-flex items-center gap-2 text-xs text-zinc-400 font-semibold">
                <VideoIcon size={12} className="text-fuchsia-300" />
                {readyCount > 0
                  ? `${readyCount} of ${LESSONS.length} episodes live · more dropping weekly`
                  : `${LESSONS.length} episodes rendering · check back soon`}
              </span>
            </div>
          </div>
        </section>

        {/* Topic filter */}
        <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="learn-topic-bar">
          {TOPICS.map((t) => {
            const active_ = topic === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTopic(t.id)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all border ${
                  active_
                    ? `text-white border-transparent bg-gradient-to-r ${t.accent} shadow-[0_4px_18px_-4px_rgba(168,85,247,0.55)]`
                    : 'text-zinc-300 border-white/10 bg-white/[0.03] hover:bg-white/[0.07] hover:border-white/20'
                }`}
                data-testid={`learn-topic-${t.id}`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Lesson grid */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="learn-grid">
          {filtered.map((l) => (
            <LessonCard key={l.id} lesson={l} onPlay={setActive} />
          ))}
        </section>

        {filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center mt-6">
            <p className="text-white font-semibold">No lessons in this topic yet</p>
            <p className="text-zinc-400 text-xs mt-1">Pick a different topic — more episodes are on the way.</p>
          </div>
        )}

        {/* Footer CTA */}
        <section className="mt-14 rounded-3xl border border-fuchsia-400/20 bg-gradient-to-r from-fuchsia-500/10 via-purple-500/5 to-cyan-400/10 p-8 text-center" data-testid="learn-bottom-cta">
          <Sparkles size={24} className="mx-auto text-fuchsia-300 mb-3" />
          <h2 className="font-syne text-2xl sm:text-3xl font-bold text-white">Got a topic you want covered?</h2>
          <p className="text-zinc-300 text-sm mt-2 max-w-xl mx-auto">
            Drop a request in the feedback box — every requested lesson goes into the production queue.
          </p>
          <Link
            to="/feedback"
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/15 text-white text-sm font-bold border border-white/20 transition-all"
            data-testid="learn-bottom-cta-btn"
          >
            Request a lesson <ArrowRight size={14} />
          </Link>
        </section>
      </main>

      <Footer />

      <VideoPlayerModal lesson={active} onClose={() => setActive(null)} />
    </div>
  );
}
